import {Injectable, PLATFORM_ID, inject} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {
  HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest
} from '@angular/common/http';
import {Observable, catchError, throwError} from 'rxjs';
import {ToastsService} from '../services/toasts.service';
import {ToastType} from '../enums/ToastType';

/** Запрос с собственным фоллбэком (например, параметры калькуляторов) — без тоста при ошибке. */
export const SILENT_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * Понятный тост на упавшие HTTP-запросы — чтобы юзер видел сбой,
 * а не только красное в консоли. Одно и то же сообщение показывается
 * не чаще, чем раз в 30 секунд: фоновые опросы (тикер, рынок) иначе
 * завалили бы экран одинаковыми тостами. Ошибка уходит дальше —
 * собственные обработчики компонентов продолжают работать.
 */
@Injectable()
export class ErrorToastInterceptor implements HttpInterceptor {
  private toasts = inject(ToastsService);


  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private lastMsg = '';
  private lastAt = 0;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && !req.context.get(SILENT_ERROR_TOAST)) this.notify(err);
        return throwError(() => err);
      })
    );
  }

  private notify(err: HttpErrorResponse): void {
    if (!this.isBrowser) return;
    // 404 — штатный ответ (нет статьи/монеты/страницы списка): страницы показывают его сами
    if (err.status === 404) return;

    const msg = this.describe(err);
    const now = Date.now();
    if (msg === this.lastMsg && now - this.lastAt < 30_000) return;
    this.lastMsg = msg;
    this.lastAt = now;

    this.toasts.pushToast({message: msg, type: ToastType.Danger});
  }

  private describe(err: HttpErrorResponse): string {
    if (err.status === 0) return 'Нет связи с сервером — проверьте интернет';
    if (err.status === 401) return 'Нужно войти в аккаунт';
    if (err.status === 403) return 'Недостаточно прав';
    if (err.status === 429) return 'Слишком много запросов — повторите чуть позже';
    if (err.status >= 500) return 'Сервер временно недоступен — попробуйте позже';
    return `Ошибка запроса (код ${err.status})`;
  }
}
